import * as React from 'react';
import {FunctionComponent, useMemo} from 'react';
import {Stroke} from '../../../../../Models/Stroke';
import {formatTime} from '../../../../Support/Formatting/formatTime';
import {formatDistance} from '../../../../Support/Formatting/formatDistance';
import {formatAverageStrokeRate} from '../../../../Support/Formatting/formatStrokeRate';
import {formatAverageSplit} from '../../../../Support/Formatting/formatSplit';
import {formatAveragePower} from '../../../../Support/Formatting/formatPower';
import {formatAverageStrokeLength} from '../../../../Support/Formatting/formatStrokeLength';
import {formatAverageEnergy} from '../../../../Support/Formatting/formatEnergy';
import {formatAveragePeakForce} from '../../../../Support/Formatting/formatPeakForce';
import {formatAveragePeakForcePosition} from '../../../../Support/Formatting/formatPeakForcePosition';
import {formatAverageDriveTime} from '../../../../Support/Formatting/formatDriveTime';
import {formatAverageRecoverTime} from '../../../../Support/Formatting/formatRecoverTime';
import {DualRange} from '../../../Form/DualRange';
import {Duration} from '../../../../../Domain/Duration';
import {calculateRangeOverview} from '../../../../../Strokes/calculateRangeOverview';
import {RangeInput} from '../../../../../Models/RangeInput';
import {Content} from './Components/Content';
import {formatAveragePowerSplit} from '../../../../Support/Formatting/formatPowerSplit';
import {PowerMode, useSettings} from '../../../../Settings/settings';
import {formatAverageRelativePeakForcePosition} from '../../../../Support/Formatting/formatRelativePeakForcePosition';

interface OwnProps {
    strokes: Stroke[];
    range: RangeInput | null;
    onRangeChange?: (strokeRange: RangeInput | null) => void;
}

export const RangeStrokeAnalyzer: FunctionComponent<OwnProps> = ({strokes, range, onRangeChange}) => {
    const overview = useMemo(() => {
        return calculateRangeOverview(
            {
                start: range === null ? Duration.zero() : range.start,
                end: range === null ? strokes[strokes.length - 1].totalTime : range.end,
                predetermined: {},
            },
            strokes,
        );
    }, [range === null ? '-' : range, strokes]);

    const {powerMode} = useSettings();

    return (
        <>
            <Content
                topBuckets={[
                    {value: formatTime(overview.totalDuration), label: 'Time'},
                    {value: formatDistance(overview.totalDistance), label: 'Distance'},
                    {value: formatAverageStrokeRate(overview.averageStrokeRate), label: 'Ø Stroke rate'},
                ]}
                dataBuckets={[
                    {value: formatAverageSplit(overview.averageSpeed), label: 'Ø Split'},
                    {
                        value:
                            powerMode === PowerMode.Split
                                ? formatAveragePowerSplit(overview.averageSpeed)
                                : formatAveragePower(overview.averagePower),
                        label: powerMode === PowerMode.Split ? 'Ø Split Power' : 'Ø Power',
                    },
                    {value: formatAverageStrokeLength(overview.averageStrokeLength), label: 'Ø Stroke length'},
                    {value: formatAverageEnergy(overview.averageEnergy), label: 'Ø Energy per stroke'},
                    {value: formatAveragePeakForce(overview.averagePeakForce), label: 'Ø Peak force'},
                    {
                        value: formatAveragePeakForcePosition(overview.averagePeakForcePosition),
                        label: 'Ø Peak force position',
                    },
                    {
                        value: formatAverageRelativePeakForcePosition(overview.relativePeakForcePosition),
                        label: 'Ø Rel. Peak Pos.',
                    },
                    {value: formatAverageDriveTime(overview.averageDriveTime), label: 'Ø Drive time'},
                    {value: formatAverageRecoverTime(overview.averageRecoverTime), label: 'Ø Recover time'},
                ]}
                curve={overview.curve}
            />
            {onRangeChange && (
                <DualRange
                    value={{
                        min: range?.start.milliseconds || 0,
                        max: range?.end.milliseconds || strokes[strokes.length - 1].totalTime.milliseconds,
                    }}
                    onChange={(r) => {
                        onRangeChange({
                            start: Duration.fromMilliseconds(r.min),
                            end: Duration.fromMilliseconds(r.max),
                            predetermined: {},
                        });
                    }}
                    min={0}
                    max={strokes[strokes.length - 1].totalTime.milliseconds}
                    step={10}
                />
            )}
        </>
    );
};
